import { DEFAULT_SERVICE_DURATION } from '@constant/appointment';
import { SERVICES_INDEX } from '@constant/common';
import { showSuccess, updatePdpItem } from '@context/actions';
import { replaceAppointmentServices } from '@context/actions/appointment';
import { CloseIcon, ErrorInfoIcon, IconFrame } from '@element/icons';
import ConfirmationModal from '@module/confirmationModal';
import { getStaffByTenantAndStoreId, getStaffIds, getStaffSlot } from '@storeData/staff';
import { navigateTo } from '@util/routerService';
import { checkStaffWeekOff, formattedDuration, formatTimeTo12Hr, formatToDateFormat, getDateObj, getDisabledDates, getNextDayWarningMessage, getStoreStartMins, getSystemBusinessDate, isActiveAtBinaryIndex, processTaxList, removeObjRef } from '@util/utils';
import { forwardRef, Fragment, memo, useEffect, useRef, useState } from 'react';
import DatePicker from "react-datepicker";
import { useDispatch, useSelector } from 'react-redux';

function AppointmentServiceModal({ item, onClose }) {

    const modalRef = useRef(null);
    const dispatch = useDispatch();
    const appointmentItems = useSelector((state: any) => state.appointmentServices);
    const [selectedItem, setSelectedItem] = useState(item)
    const [error, setError] = useState({ id: '', text: '' });
    const storeData = useSelector((state: any) => state.store ? state.store.storeData : null);
    const [allExpertSlotData, setAllExpertSlotData] = useState<any[]>([]);
    const [availableExpertSlotData, setAvailableExpertSlotData] = useState([]);
    const [availableSlots, setAvailableSlots] = useState([])
    const { configData } = useSelector((state: any) => state.store ? state.store.storeData : null);
    const APPOINTMENT_SLOT_GAP_IN_MINS = configData.storeConfig?.appointmentConfig.defaultSlotTime ? configData.storeConfig?.appointmentConfig.defaultSlotTime : 30
    const [isLoading, setIsLoading] = useState(false);
    const [dateChangeConfirmationModal, setDateChangeConfirmationModal] = useState({
        secondaryButtonText: "",
        openModal: false,
        title: '',
        message: '',
        buttonText: '',
        handleClose: null
    })
    const store = useSelector((state: any) => state);
    const [weekOffDate, setWeekOffDate] = useState("");
    const [disabledBooking, setDisabledBooking] = useState(false)
    const languageData: any = store.languageKeywords
    const activeLanguage: any = store.activeLanguage
    const businessNow = getSystemBusinessDate(configData?.dayWiseTiming);

    const DatePickerInput = forwardRef((props: any, ref: any) => <button className={`date-picker-button ${error.id == 'date' ? "error" : ""}`}
        onClick={props.onClick} ref={ref}>
        {languageData["Appointment Date"] ? languageData["Appointment Date"] : "Appointment Date"} {props.value ? `: ${props.value}` : <div className='guided-label'>{languageData["Click to select date"] ? languageData["Click to select date"] : "Click to select date"}</div>}
    </button>);
    const [activeStep, setActiveStep] = useState<"" | "date" | "staff" | "slot">("date");
    // Tapping the header of the open step closes it, the same as a select control.
    const toggleStep = (step: "date" | "staff" | "slot") => {
        setActiveStep((current) => (current === step ? "" : step));
    };
    const checkForWeekOff = (date) => {
        const dayShort = date.toLocaleString("en-US", { weekday: "short" });
        if (configData?.weeklyOff?.includes(dayShort)) {
            setWeekOffDate(getDateObj(date).dateObj)
            return true
        } else {
            setWeekOffDate("")
            return false
        }
    }

    const loadSlotsForStaff = (itemWithStaff: any) => {
        if (!itemWithStaff?.staff?.id || !itemWithStaff?.date) return;
        if (checkStaffWeekOff(itemWithStaff.date, itemWithStaff.staff?.weeklyOff)) {
            setAvailableSlots([]);
            return;
        }
        setIsLoading(true);
        getStaffSlot(
            getDateObj(new Date(itemWithStaff.date)).dateObj,
            itemWithStaff.staff.id,
            storeData?.storeId,
            storeData?.tenantId
        ).then((res: any) => {
            setIsLoading(false);
            setAvailableSlots(res.data || []);
            itemWithStaff.staff.slots = res.data;
            setSelectedItem({ ...itemWithStaff });
        }).catch(() => {
            setAvailableSlots([]);
            setIsLoading(false);
        });
    };

    /**
     * `advanceStep` is only for a date the visitor just picked. On the modal's first render the
     * step must stay put, otherwise the open panel jumps from date to slot as the staff request
     * resolves and the whole form appears to flicker.
     */
    const applySingleExpertIfOnlyOne = (
        staffList: any[],
        itemCopy: any,
        selectedDate: Date,
        advanceStep = true
    ) => {
        const selectableStaff = staffList.filter(
            (staff: any) => !checkStaffWeekOff(selectedDate, staff.weeklyOff)
        );
        if (selectableStaff.length !== 1) return false;

        itemCopy.staff = selectableStaff[0];
        itemCopy.slot = null;
        setSelectedItem(itemCopy);
        if (advanceStep) setActiveStep("slot");
        loadSlotsForStaff(itemCopy);
        return true;
    };

    useEffect(() => {
        if (item) {
            const bookingOnStoreOff = Boolean(storeData.configData.storeConfig.appointmentConfig?.bookingOnStoreOff)
            if (storeData.configData.storeOff && !bookingOnStoreOff) {
                setDisabledBooking(true);
            }
            const selectedItemCopy = item?.date ? item : { ...item, date: businessNow };
            setSelectedItem(selectedItemCopy)
            setIsLoading(true)
            getStaffByTenantAndStoreId(storeData.tenantId, storeData.storeId)
                .then((expertsList: any) => {
                    if (expertsList) {
                        if (expertsList.length != 0) {
                            setAllExpertSlotData(expertsList.filter((e: any) => e.active));
                            setAvailableExpertSlotData(expertsList.filter((e: any) => e.active));
                            let availableStaff = [];
                            if (selectedItemCopy.experts) {
                                selectedItemCopy.experts.map((expert: any) => {
                                    let alreadyAdded = availableStaff.length != 0 ? availableStaff.filter((s) => s.id == expert.id) : [];
                                    if (alreadyAdded.length == 0) {
                                        let staffs = expertsList.filter((s) => s.active && s.id == expert.id);
                                        staffs.length != 0 && availableStaff.push(staffs[0])
                                    }
                                })
                            }
                            if (availableStaff.length != 0) {
                                setAllExpertSlotData(availableStaff);
                                getStaffIds(getDateObj(new Date(selectedItemCopy.date)).dateObj, storeData.storeId, storeData.tenantId)
                                    .then((res: any) => {
                                        const availableIds = Array.isArray(res?.data) ? res.data : (res || []);
                                        const filteredStaff = availableStaff.filter((staff: any) => availableIds.includes(staff.id));
                                        setAvailableExpertSlotData(filteredStaff);
                                        const autoSelected = applySingleExpertIfOnlyOne(
                                            filteredStaff,
                                            { ...selectedItemCopy },
                                            new Date(selectedItemCopy.date),
                                            false
                                        );
                                        if (!autoSelected) setIsLoading(false);
                                    })
                                    .catch(() => {
                                        setAvailableExpertSlotData([]);
                                        setIsLoading(false)
                                    });
                            } else {
                                setAllExpertSlotData([]);
                                setAvailableExpertSlotData([]);
                                setIsLoading(false)
                            }
                        } else {
                            setAllExpertSlotData([]);
                            setAvailableExpertSlotData([]);
                            setIsLoading(false)
                        }
                    } else {
                        setIsLoading(false)
                    }
                })
                .catch((e) => {
                    setIsLoading(false)
                    // dispatch(showError(e.error));
                })
        }
    }, [item])

    const onOutsideClick = (event: any) => {
        if (modalRef.current && !modalRef.current.contains(event.target) && !dateChangeConfirmationModal.openModal) {
            onClose();
        }
    }

    const onInputChange = (from, value) => {
        const selectedItemCopy = removeObjRef(selectedItem)
        selectedItemCopy.slot = null;
        selectedItemCopy[from] = value;
        setSelectedItem(selectedItemCopy)
        setError({ id: '', text: '' });
        if (activeStep === "date") {
            setIsLoading(true);
            const staffData = allExpertSlotData.length ? [...allExpertSlotData] : [...availableExpertSlotData];
            getStaffIds(getDateObj(new Date(value)).dateObj, storeData.storeId, storeData.tenantId)
                .then((res: any) => {
                    const availableIds = Array.isArray(res?.data) ? res.data : (res || []);
                    const filteredStaff = staffData.filter((staff: any) => availableIds.includes(staff.id));
                    setAvailableExpertSlotData(filteredStaff);

                    if (applySingleExpertIfOnlyOne(filteredStaff, selectedItemCopy, new Date(value))) {
                        return;
                    }

                    if (selectedItemCopy.staff && !availableIds.includes(selectedItemCopy.staff.id)) {
                        selectedItemCopy.staff = null;
                        selectedItemCopy.slot = null;
                        setSelectedItem(selectedItemCopy);
                        setIsLoading(false);
                    } else if (selectedItemCopy.staff?.id) {
                        loadSlotsForStaff(selectedItemCopy);
                    } else {
                        setIsLoading(false);
                    }
                })
                .catch(() => {
                    setAvailableExpertSlotData([]);
                    setIsLoading(false);
                });
            renderStaffList()
            setActiveStep("staff")
        } else if (activeStep === "staff") {
            setActiveStep("slot")
        }

        if (from === 'staff' && selectedItemCopy.staff?.id && selectedItemCopy.date) {
            loadSlotsForStaff(selectedItemCopy);
        }
        if (from == 'date') {
            setDateChangeConfirmationModal({
                secondaryButtonText: "",
                openModal: false,
                title: '',
                message: '',
                buttonText: '',
                handleClose: null
            })
        }
    }

    const onDateChange = (updatedDate) => {
        if (checkForWeekOff(updatedDate)) return
        if (Boolean(appointmentItems.length) && (getDateObj(new Date(appointmentItems[0].date)).dateObj !== getDateObj(new Date(updatedDate)).dateObj)) {
            setDateChangeConfirmationModal({
                openModal: true,
                secondaryButtonText: `${languageData["Change"] ? languageData["Change"] : "Change"}`,
                title: `${languageData["Date Change Confirmation"] ? languageData["Date Change Confirmation"] : "Date Change Confirmation"}`,
                message: `${languageData["If you change the date, then the previously added service date and slot get reset"] ? languageData["If you change the date, then the previously added service date and slot get reset"] : "If you change the date, then the previously added service date and slot get reset."}`,
                buttonText: `${languageData["Yes"] ? languageData["Yes"] : "Yes"}`,
                handleClose: (status) => status ? onInputChange('date', updatedDate) :
                    setDateChangeConfirmationModal({
                        openModal: false,
                        secondaryButtonText: "",
                        title: '',
                        message: '',
                        buttonText: '',
                        handleClose: null
                    })
            })
        } else {
            //for the first time setting date
            onInputChange('date', updatedDate);
        }
    }

    const renderStaffList = () => {
        let staffData = [...availableExpertSlotData];
        const selectedDate = selectedItem?.date ? new Date(selectedItem.date) : businessNow;
        staffData = staffData.filter(staff => !checkStaffWeekOff(selectedDate, staff.weeklyOff));
        return <>
            {Boolean(staffData?.length) ? <>
                {staffData.map((staff: any, i: number) => {
                    return <Fragment key={i}>
                        <div className={`staff-details ${selectedItem.staff?.id == staff.id ? "active" : ''}`} onClick={() => onInputChange("staff", staff)}>{staff.firstName} {staff.lastName}</div>
                    </Fragment>
                })}
            </> : <>
                <div className='unavailable-data'>{languageData["Staff Unavailable"] ? languageData["Staff Unavailable"] : "Staff Unavailable"}</div>
            </>}
        </>
    }

    const onSlotClick = (slot: any) => {
        setSelectedItem({ ...selectedItem, slot: slot })
    }

    const timeToMins = (time) => {
        if (!time || typeof time !== 'string' || !time.includes(':')) return 0;
        const [hours, minutes] = time.split(':').map(Number);
        return hours * 60 + minutes;
    };

    const getServiceDuration = (duration, durationType) => {
        if (durationType?.includes('hrs')) {
            return (parseFloat(duration) * 60);
        }
        return parseFloat(duration) || DEFAULT_SERVICE_DURATION;
    };

    const renderSlotsList = () => {
        if (!selectedItem.date && !selectedItem?.staff?.id) {
            return <div className='unavailable-data'>
                {languageData["Please select date & staff to view available slots"] ? languageData["Please select date & staff to view available slots"] : "Please select date & staff to view available slots"}
            </div>
        }
        if (!selectedItem.date) {
            return <div className='unavailable-data'>
                {languageData["Please select date to view available slots"] ? languageData["Please select date to view available slots"] : "Please select date to view available slots"}
            </div>
        }
        if (!selectedItem?.staff?.id) {
            return <div className='unavailable-data'>
                {languageData["Please select expert to view available slots"] ? languageData["Please select expert to view available slots"] : "Please select expert to view available slots"}
            </div>
        }
        if (availableSlots.length == 0) {
            return <div className='unavailable-data'>
                {languageData["Slots are unavailable for this date & staff"] ? languageData["Slots are unavailable for this date & staff"] : "Slots are unavailable for this date & staff"}
            </div>
        }

        let availableSlotsCopy = [...availableSlots];
        availableSlotsCopy.pop();
        const bookedTimePoints = new Set();
        appointmentItems.forEach((serviceItem: any) => {
            if (serviceItem.staff && serviceItem.staff.id == selectedItem.staff.id && serviceItem.slot) {
                const startTimeInMins = timeToMins(serviceItem.slot);
                const durationInMins = getServiceDuration(serviceItem.duration, serviceItem.durationType);
                for (let i = 0; i < durationInMins; i += APPOINTMENT_SLOT_GAP_IN_MINS) {
                    bookedTimePoints.add(startTimeInMins + i);
                }
            }
        });

        const storeStartMins = getStoreStartMins(selectedItem.date, configData);
        const nextDayMessage = getNextDayWarningMessage(availableSlotsCopy, storeStartMins, selectedItem.date, configData, formatToDateFormat, formatTimeTo12Hr);

        return (
            <>
                <div className="slot-time-outer">
                    {availableSlotsCopy.map((storeSlot, i) => {
                        const currentSlotInMins = timeToMins(storeSlot.slot);
                        const serviceDuration: any = getServiceDuration(selectedItem.duration, selectedItem.durationType);
                        const requiredSlotsCount = Math.ceil(serviceDuration / APPOINTMENT_SLOT_GAP_IN_MINS);
                        const isSelected = bookedTimePoints.has(currentSlotInMins);
                        let overlap = false;
                        if (!isSelected) {
                            for (let j = APPOINTMENT_SLOT_GAP_IN_MINS; j < serviceDuration; j += APPOINTMENT_SLOT_GAP_IN_MINS) {
                                if (bookedTimePoints.has(currentSlotInMins + j)) {
                                    overlap = true;
                                    break;
                                }
                            }
                        }

                        let hasTime = true;
                        if (requiredSlotsCount > 1) {
                            if (i + requiredSlotsCount > availableSlotsCopy.length) {
                                hasTime = false;
                            } else {
                                for (let k = 1; k < requiredSlotsCount; k++) {
                                    if (!availableSlotsCopy[i + k]?.isAvailable) {
                                        hasTime = false;
                                        break;
                                    }
                                }
                            }
                        }

                        const canBeSelected = storeSlot.isAvailable && !isSelected && !overlap && hasTime;

                        let slotText = formatTimeTo12Hr(storeSlot.slot);
                        if (!storeSlot.isAvailable) {
                            slotText = "Booked";
                        } else if (isSelected) {
                            slotText = "Selected";
                        }
                        return (
                            <div key={storeSlot.slot} onClick={() => canBeSelected && (setSelectedItem({ ...selectedItem, slot: storeSlot.slot }), setError({ id: '', text: '' }), setActiveStep(""))}
                                className={`${canBeSelected ? '' : "unavailable"} ${selectedItem.slot === storeSlot.slot ? 'slot-wrap active' : "slot-wrap"}`} >
                                {slotText}
                            </div>
                        );
                    })}
                </div>
                {nextDayMessage && (
                    <div className="next-day-warning" style={{ marginTop: '12px', fontSize: '13px', color: '#ff9800', fontStyle: 'italic', lineHeight: '1.4' }}>
                        {nextDayMessage}
                    </div>
                )}
            </>
        );
    }

    const translate = (keyword: string) => languageData[keyword] || keyword;

    /**
     * Returns the first incomplete step so the message can be shown against that field
     * instead of as a detached line above the action button.
     */
    const getValidationError = () => {
        if (!selectedItem.date) {
            return { id: 'date', text: translate('Please select date to view available slots') };
        }
        if (!selectedItem?.staff?.id) {
            return { id: 'staff', text: translate('Please select expert to view available slots') };
        }
        if (!selectedItem?.slot) {
            return { id: 'slot', text: translate('Please select slot') };
        }
        return null;
    };

    const hasValidationError = () => {
        const validationError = getValidationError();
        if (!validationError) {
            setError({ id: '', text: '' });
            return false;
        }
        setError(validationError);
        return true;
    };

    const renderFieldError = (stepId: string) => {
        if (error.id !== stepId || !error.text) return null;
        return (
            <div className='field-error' role="alert">
                <ErrorInfoIcon />
                <span>{error.text}</span>
            </div>
        );
    };

    const onAddMoreClick = () => {
        if (hasValidationError()) return;
        onBookClick(false)
        onClose();
    }

    const calculateTaxes = (taxebalePrice: any, taxCharges: any) => {
        const hasTaxCharges = Boolean(taxCharges?.length);
        const taxesToProcess = hasTaxCharges ? taxCharges : configData.txchConfig || [];
        const taxList = taxesToProcess.filter(taxData =>
            taxData.active && (hasTaxCharges || (isActiveAtBinaryIndex(taxData.applyOn, SERVICES_INDEX) && !taxData.charge)));

        return processTaxList(taxList, taxebalePrice)
    }

    const onBookClick = (navigate) => {
        if (hasValidationError()) return;
        const appointmentItemsCopy = appointmentItems ? [...appointmentItems] : [];
        const serviceCopy = { ...selectedItem };
        serviceCopy.txchrgs = [];
        let price: any = parseFloat(serviceCopy.price);
        let salePrice: any = parseFloat(serviceCopy.salePrice);
        serviceCopy.price = price;
        serviceCopy.salePrice = salePrice;
        serviceCopy.txchrgs = calculateTaxes(salePrice || price, serviceCopy.taxCharges);
        appointmentItemsCopy.push(serviceCopy);
        //if date changed for current selected item then remove others staff and slot
        appointmentItemsCopy.map((item) => {
            if (getDateObj(new Date(item.date)).dateObj !== getDateObj(new Date(serviceCopy.date)).dateObj) {
                item.date = selectedItem.date;
                item.staff = null;
                item.slot = null;
            }
        })
        dispatch(updatePdpItem(null))
        dispatch(replaceAppointmentServices(appointmentItemsCopy));
        dispatch(showSuccess(`${languageData['Service Added'] ? languageData['Service Added'] : 'Service Added'}`, 2000));
        onClose();
        // dispatch(showServicesCart(true));
        navigate && navigateTo('appointment')
    }
    return (
        <div className='services-cart-modal-wrap backdrop-modal-wrapper'
            onClick={onOutsideClick}
            style={{
                height: 'calc(100vh - calc(100vh - 100%))',
                overflow: 'unset',
                maxHeight: 'calc(100vh - calc(100vh - 100%))',
                backgroundColor: '#131212b3'
            }}>
            <div className='services-cart-modal-details appointment-service-form backdrop-modal-content' ref={modalRef}>
                <div className='heading'>
                    {languageData["Service"] ? languageData["Service"] : "Service"} - {selectedItem.languages && selectedItem.languages[activeLanguage?.languageId]?.name ? selectedItem.languages[activeLanguage?.languageId]?.name : selectedItem.name}
                    <br />
                    <span> ({languageData["Duration"] ? languageData["Duration"] : "Duration"} : {formattedDuration(selectedItem)})</span>
                </div>
                <div className="modal-close" onClick={onClose}>
                    <IconFrame width={20} height={20}><CloseIcon /></IconFrame>
                </div>
                <div className='services-list-wrap main-wrapper appointment-wrap appt-service-wrap'>
                    <div className={`main-label-wrapper ${activeStep === "date" ? "open" : ""} ${error.id == 'date' ? "error" : ""}`} onClick={() => toggleStep("date")}>
                        <div className="apt-label">{languageData["Appointment Date"] ? languageData["Appointment Date"] : "Appointment Date"}:</div>
                        {selectedItem.date ? new Date(selectedItem.date).toLocaleDateString() : <div className='guided-label'>{languageData["Click to select date"] ? languageData["Click to select date"] : "Click to select date"}</div>}
                    </div>
                    <div className={`date-picker-wrap card ${activeStep === "date" ? "expandable" : ""}`}>
                        <DatePicker
                            excludeDates={getDisabledDates(configData)}
                            selected={selectedItem.date ? new Date(selectedItem.date) : businessNow}
                            onChange={(date) => onDateChange(date)}
                            inline
                            dateFormat={configData.dateFormat || "dd-MM-yyyy"}
                            minDate={businessNow}
                        />
                    </div>
                    {renderFieldError('date')}
                    {Boolean(weekOffDate) && <div className='field-error' role="alert">
                        <ErrorInfoIcon />
                        <span>{translate('We are un-serviceable on')} {weekOffDate}.</span>
                    </div>}
                    <div className={`main-label-wrapper ${activeStep === "staff" ? "open" : ""} ${error.id == 'staff' ? "error" : ""}`} onClick={() => toggleStep("staff")}>
                        <div className="apt-label">{languageData["Expert"] ? languageData["Expert"] : "Expert"}:</div>
                        {selectedItem.staff ? ` ${selectedItem?.staff?.firstName} ${selectedItem?.staff?.lastName}` : <div className='guided-label'>{languageData["Click to select expert"] ? languageData["Click to select expert"] : "Click to select expert"}</div>}
                    </div>
                    <div className={`card staff-list ${activeStep === "staff" ? "expandable" : ""}`} >
                        {renderStaffList()}
                    </div>
                    {renderFieldError('staff')}

                    <div className={`main-label-wrapper ${activeStep === "slot" ? "open" : ""} ${error.id == 'slot' ? "error" : ""}`} onClick={() => toggleStep("slot")}>
                        <div className="apt-label">{languageData["Slot"] ? languageData["Slot"] : "Slot"}:</div>
                        {selectedItem.slot ? formatTimeTo12Hr(selectedItem.slot) : <div className='guided-label'>{languageData["Click to select slot"] ? languageData["Click to select slot"] : "Click to select slot"}</div>}
                    </div>
                    <div className={`card slot-list ${activeStep === "slot" ? "expandable" : ""}`}>
                        {renderSlotsList()}
                    </div>
                    {renderFieldError('slot')}
                    {/* {appointmentItems?.map((service: any) => {
                        return <div className="service-details" key={Math.random()}>
                            <div className="service-name">{service.name}
                                {service.variations && service.variations[0] && <>
                                    &nbsp;- {service.variations[0]?.name}
                                </>}
                            </div>

                        </div>
                    })} */}
                </div>
                {!Boolean(disabledBooking) && <div className={`btn-wrap ${weekOffDate ? "disabled" : ""}`}>
                    {/* <div className="primary-btn" onClick={onAddMoreClick}>{languageData["Add more services"] ? languageData["Add more services"] : "Add more services"}</div> */}
                    <div className="primary-btn" onClick={() => onBookClick(true)}>{languageData["Book Appointment"] ? languageData["Book Appointment"] : "Continue"}</div>
                </div>}
            </div>
            <ConfirmationModal {...dateChangeConfirmationModal} />
        </div>
    )
}

export default memo(AppointmentServiceModal)
